import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { DEFAULT_COUNTRY } from 'addresses/constants';
import { useAuthUser } from 'auth/hooks';
import { DATE_MONTH_YEAR_SHORT_FORMAT, NOT_AVAILABLE } from 'core/constants';
import { FormProvider, useNotificationContext } from 'core/states';
import { formatDate } from 'core/utils';
import { useProjectActions } from 'projects/hooks';
import { mapUpsertProjectForm } from 'projects/mappers';
import { IAddProjectForm, IProject, IProjectDetails } from 'projects/types';

import { addProjectSchema } from './schema';
import { UpsertProjectFormFields } from './UpsertProjectFormFields';

interface IProps {
  isOpen: boolean;
  onClose: (project?: IProject) => void;
  initialValues?: IProject;
  level?: number;
  onSubmitSuccess?: (project: IProject) => void;
  project?: IProjectDetails;
}

export const UpsertProject = ({
  isOpen = false,
  onClose,
  initialValues,
  level = 0,
  onSubmitSuccess,
  project,
}: IProps) => {
  const { user } = useAuthUser();
  const { clearNotificationTimeout } = useNotificationContext();
  const { upsertProject } = useProjectActions();

  const isEdit = useMemo(() => !!project, [project]);

  const defaultValues = useMemo(() => {
    if (isEdit && project) {
      return {
        ...project,
        name: project.name,
        validSince: project.validSince ? dayjs(project.validSince) : null,
        validUntil: project.validUntil ? dayjs(project.validUntil) : null,
        closeDate: project.closeDate
          ? formatDate(project.closeDate, false, DATE_MONTH_YEAR_SHORT_FORMAT)
          : null,
        verticalMarket: project.market,
        address: {
          ...project.addressArr,
          country: project.addressArr?.countryId
            ? {
                id: project.addressArr.countryId,
                name: project.addressArr.countryName,
                abbreviation: project.addressArr.countryAbbreviation,
              }
            : null,
          city:
            project.addressArr &&
            project.addressArr.city &&
            project.addressArr.city !== NOT_AVAILABLE
              ? project.addressArr.city
              : null,
          zip:
            project.addressArr && project.addressArr.zip && project.addressArr.zip !== NOT_AVAILABLE
              ? project.addressArr.zip
              : null,
        },
      };
    }

    return {
      name: initialValues?.name,
      address: {
        country: DEFAULT_COUNTRY,
      },
    };
  }, [isEdit, project, initialValues]);

  const handleOnClose = useCallback(
    (reset: UseFormReset<IAddProjectForm>) => {
      onClose();

      reset(defaultValues);

      clearNotificationTimeout();
    },
    [onClose, defaultValues, clearNotificationTimeout]
  );

  const onSubmit = useCallback(
    (reset: UseFormReset<IAddProjectForm>) => async (data: IAddProjectForm) => {
      const { success, project } = await upsertProject(mapUpsertProjectForm(data));

      if (!success) {
        return;
      }

      setTimeout(() => {
        handleOnClose(reset);

        onSubmitSuccess?.(project);
      }, 1000);
    },
    [upsertProject, handleOnClose, onSubmitSuccess]
  );

  return (
    <FormProvider
      schema={addProjectSchema(user.companySettings)}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <UpsertProjectFormFields
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        user={user}
        level={level}
        isEdit={isEdit}
      />
    </FormProvider>
  );
};
